import { gql } from '@urql/core';
import React, { useMemo } from 'react';
import { Espin_Role_Enum, PlayerProfileInfoFragment } from '../../../generated/spin-graphql';
import { PictureWithFallback } from '../../sections/PictureWithFallback';
import { PlayerProfileUserControls } from './PlayerProfileUserControls';
import { PlayerSocialDisplays } from './PlayerSocialDisplays';
import { useAppState } from '../../../client/providers/AppProvider';

type Props = {
    player: PlayerProfileInfoFragment;
};

gql`
    fragment PlayerProfileInfo on espin_player_profile {
        ...PlayerProfileUserControls
        spin_username
        first_name
        last_name
        user {
            subscription_status {
                is_subscribed
            }
        }
        social {
            ...PlayerSocialDisplays
        }
        address {
            city
            state
            country
        }
        profile_image {
            ...FormFile
        }
        linked_high_school_details {
            name
            slug
        }
        college_name
        hs_name
        is_hs
        ...PlayerProfileUserControls
    }
`;
export const PlayerProfileInfo: React.FC<Props> = ({ player }) => {
    const { user, subtier } = useAppState();
    const { city, state, country } = player.address;
    const locationText = country === 'United States' ? `${city}, ${state}` : `${city}, ${country}`;

    const schoolText = useMemo(() => {
        if (player.is_hs && !!player.linked_high_school_details) {
            if (user?.role === Espin_Role_Enum.EspinCollegeUser && subtier >= 2) {
                return (
                    <p className="player-location">
                        <a href={`/high-schools/${player.linked_high_school_details.slug}`}>
                            {player.linked_high_school_details.name}
                        </a>
                    </p>
                );
            } else {
                return <p className="player-location">{player.linked_high_school_details.name}</p>;
            }
        } else if (player.is_hs && !!player.hs_name) {
            return <p className="player-location">{player.hs_name}</p>;
        } else if (!player.is_hs && !!player.college_name) {
            return <p className="player-location">{player.college_name}</p>;
        } else {
            return <></>;
        }
    }, [user, subtier, player]);

    return (
        <div className="player-profile-info">
            <div className="player-profile-image">
                <PictureWithFallback
                    icon={player.profile_image}
                    width={100}
                    height={100}
                    alt={`${player.spin_username}} profile picture`}
                />
            </div>
            <div className="bio">
                <h1 className="player-gamertag">{player.spin_username}</h1>
                <p className="player-name">
                    {player.first_name} {player.last_name}
                </p>
                {schoolText}
                <p className="player-location">{locationText}</p>
                {player.user.subscription_status?.is_subscribed && <PlayerSocialDisplays social={player.social} />}
            </div>
            <PlayerProfileUserControls player={player} />
        </div>
    );
};
